// File: src/pages/candidate/InterviewDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

export default function InterviewDetailPage() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false); // Trạng thái khi bấm nút
    const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDetail = async () => {
            const token = localStorage.getItem('skillmatch_token');
            try {
                // Gọi API lấy chi tiết candidate (đã populate job và companyName)
                const res = await fetch(`${API_URL}/api/candidates/${id}`, {
                    headers: { 'x-auth-token': token }
                });
                if (res.ok) {
                    const result = await res.json();
                    setData(result);
                } else {
                    alert("Không tìm thấy dữ liệu phỏng vấn");
                    navigate('/candidate/my-interviews');
                }
            } catch (err) { console.error(err); } 
            finally { setLoading(false); }
        };
        fetchDetail();
    }, [id, API_URL, navigate]);

    // HÀM XỬ LÝ PHẢN HỒI (Accept / Reschedule)
    const handleResponse = async (status) => {
        let note = '';
        if (status === 'Reschedule') {
            note = prompt("Vui lòng nhập lý do và thời gian bạn đề xuất:");
            if (!note) return; // Nếu bấm hủy thì thôi
        }

        if (!window.confirm(status === 'Accepted' ? "Bạn xác nhận sẽ tham gia buổi phỏng vấn này?" : "Bạn chắc chắn muốn xin dời lịch?")) return;

        setIsProcessing(true);
        const token = localStorage.getItem('skillmatch_token');

        try {
            const res = await fetch(`${API_URL}/api/candidates/${id}/interview-response`, {
                method: 'PATCH',
                headers: { 
                    'Content-Type': 'application/json',
                    'x-auth-token': token 
                },
                body: JSON.stringify({ 
                    responseStatus: status, 
                    note: note 
                })
            });

            if (res.ok) {
                alert(status === 'Accepted' ? "Đã xác nhận tham gia thành công!" : "Đã gửi yêu cầu dời lịch cho nhà tuyển dụng.");
                // Reload lại dữ liệu để cập nhật giao diện
                const newData = { ...data };
                if (newData.interview) newData.interview.status = status;
                setData(newData);
            } else {
                alert("Có lỗi xảy ra. Vui lòng thử lại.");
            }
        } catch (err) {
            console.error(err);
            alert("Lỗi kết nối server.");
        } finally {
            setIsProcessing(false);
        }
    };

    if (loading) return (
        <div className="min-h-screen pt-24 flex items-center justify-center bg-[#F8FAFC]">
            <span className="material-symbols-outlined animate-spin text-3xl text-blue-600">sync</span>
        </div>
    );
    if (!data || !data.interview) return <div className="pt-32 text-center">Không tìm thấy thông tin phỏng vấn.</div>;

    const { interview, jobId } = data;
    const isOnline = interview.type === 'Online';
    const isAccepted = interview.status === 'Accepted';
    const isReschedule = interview.status === 'Reschedule';

    // Lấy tên công ty từ populate
    const companyName = jobId?.companyName || "Công ty tuyển dụng";

    return (
        <div className="min-h-screen bg-[#F8FAFC] pt-28 pb-20 px-4">
            <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-fade-up">
                
                {/* Header */}
                <div className={`p-8 text-white relative overflow-hidden ${isAccepted ? 'bg-gradient-to-r from-green-600 to-emerald-700' : 'bg-gradient-to-r from-blue-600 to-indigo-700'}`}>
                    <div className="absolute top-0 right-0 opacity-10 transform translate-x-10 -translate-y-10">
                        <span className="material-symbols-outlined" style={{fontSize: '150px'}}>calendar_clock</span>
                    </div>
                    <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-2 opacity-90 text-sm font-medium uppercase tracking-wide">
                            {isAccepted ? <><span className="material-symbols-outlined text-lg">check_circle</span> Đã xác nhận tham gia</> : "Thư mời phỏng vấn"}
                        </div>
                        <h1 className="text-3xl font-bold mb-2">{companyName}</h1>
                        <div className="mt-4 inline-flex items-center gap-2 bg-white/20 px-4 py-1.5 rounded-full text-sm font-medium backdrop-blur-sm border border-white/10">
                            <span className="material-symbols-outlined text-[18px]">work</span>
                            Vị trí: {jobId?.title || data.position}
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="p-8">
                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Thời gian</label>
                            <p className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-600">schedule</span>
                                {new Date(interview.time).toLocaleString('vi-VN', { weekday: 'long', day: '2-digit', month: '2-digit', hour: '2-digit', minute:'2-digit' })}
                            </p>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Hình thức</label>
                            <p className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <span className="material-symbols-outlined text-green-600">
                                    {isOnline ? 'video_camera_front' : 'apartment'}
                                </span>
                                {interview.type}
                            </p>
                        </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 mb-8">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-3">
                            {isOnline ? 'Đường dẫn tham gia (Link)' : 'Địa điểm phỏng vấn'}
                        </label>
                        <div className="flex items-start gap-3">
                            <span className="material-symbols-outlined text-gray-500 mt-0.5">location_on</span>
                            {isOnline ? (
                                <a href={interview.location} target="_blank" rel="noreferrer" className="text-blue-600 font-medium hover:underline break-all text-lg">
                                    {interview.location}
                                </a>
                            ) : (
                                <p className="text-gray-900 font-medium text-lg">{interview.location}</p>
                            )}
                        </div>
                    </div>

                    {interview.note && (
                        <div className="mb-8">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-2">Ghi chú từ nhà tuyển dụng</label>
                            <p className="text-gray-700 italic bg-yellow-50 p-4 rounded-lg border border-yellow-100 border-l-4 border-l-yellow-400">
                                "{interview.note}"
                            </p>
                        </div>
                    )}

                    {/* ACTIONS - Ẩn nút nếu đã phản hồi */}
                    <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-100">
                        {!isAccepted && !isReschedule ? (
                            <>
                                <button 
                                    onClick={() => handleResponse('Accepted')}
                                    disabled={isProcessing}
                                    className="flex-1 py-3.5 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-500/30 hover:bg-blue-700 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2"
                                >
                                    {isProcessing ? 'Đang xử lý...' : <><span className="material-symbols-outlined">check</span> Xác nhận tham gia</>}
                                </button>
                                <button 
                                    onClick={() => handleResponse('Reschedule')}
                                    disabled={isProcessing}
                                    className="flex-1 py-3.5 bg-white text-gray-700 border border-gray-300 rounded-xl font-bold hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                                >
                                    <span className="material-symbols-outlined">edit_calendar</span> Xin dời lịch
                                </button>
                            </>
                        ) : (
                            <div className={`w-full py-4 text-center rounded-xl font-bold border ${isAccepted ? 'bg-green-50 text-green-700 border-green-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`}>
                                {isAccepted ? "✅ Bạn đã xác nhận tham gia buổi phỏng vấn này." : "⏳ Bạn đã gửi yêu cầu dời lịch. Vui lòng đợi phản hồi."}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}